import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil } from "lucide-react";
import { finApi, money } from "@/lib/financeApi";
import { useIsSuper } from "@/components/nds/windows/finance/InvoiceActions";

const field = "bg-black/40 border border-white/10 rounded-sm px-1.5 py-1 text-[11px] text-nex-silver focus:outline-none focus:border-nex-gold/60";

/** A money figure on the week card that a super admin can retype; saving books one dated correction line for the difference. */
export function EditMoney({ rid, w, field: f, value, className = "text-nex-silver", onDone }) {
  const sa = useIsSuper();
  const [open, setOpen] = useState(false);
  const [amt, setAmt] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const id = `${f}-${w.week_ending}`;
  const show = <span className={`font-mono ${className}`} data-testid={`weekedit-value-${id}`}>{money(value || 0)}</span>;
  if (!sa || w.ledger || w.locked) return show;  // ledger weeks read only the ledger; locked weeks change only through Correct this week
  if (!open) return <>{show}<button type="button" onClick={() => { setAmt(String(value || 0)); setNote(""); setOpen(true); }} aria-label="Type the correct amount" data-testid={`weekedit-open-${id}`}
    className="ml-1 inline-flex align-middle text-nex-muted hover:text-nex-gold"><Pencil className="h-3 w-3" /></button></>;
  const num = Number(String(amt).replace(/[$,\s]/g, ""));
  const save = async () => {
    setBusy(true);
    try {
      await finApi.post(`/flow/dues/${rid}/fix`, { action: "adjust", week: w.week_ending, field: f, target: num, note });
      toast.success("Correction booked"); setOpen(false); onDone?.(); window.dispatchEvent(new CustomEvent("fin:money-changed"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); } finally { setBusy(false); }
  };
  const ok = amt !== "" && !Number.isNaN(num) && (note.match(/[A-Za-z]/g) || []).length >= 5;
  return (
    <span className="inline-flex flex-wrap items-center gap-1 align-middle" data-testid={`weekedit-form-${id}`}>
      <input inputMode="decimal" value={amt} onChange={(e) => setAmt(e.target.value)} className={`${field} w-20 text-right`} data-testid={`weekedit-amount-${id}`} autoFocus />
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why (5+ letters)" className={`${field} w-32`} data-testid={`weekedit-note-${id}`} />
      <button type="button" onClick={save} disabled={!ok || busy} data-testid={`weekedit-save-${id}`} className="text-nex-gold disabled:opacity-40 inline-flex items-center gap-1">{busy && <Loader2 className="h-3 w-3 animate-spin" />}Save</button>
      <button type="button" onClick={() => setOpen(false)} disabled={busy} data-testid={`weekedit-cancel-${id}`} className="text-nex-muted">Cancel</button>
    </span>
  );
}
